/**
 * VisitaMed Message Export Service
 * Serviço de exportação de notas para formato de mensagem
 */

import type { Note } from '@/models/note';

/** Estrutura de tags agrupadas */
export interface TagGroupData {
  tag: string;
  notes: Note[];
}

/** Escopo de exportação por data */
export interface DateScope {
  type: 'date';
  date: string;
  tags: TagGroupData[];
  /** Título opcional do bloco (fallback: Pendências) */
  title?: string;
}

/** Escopo de exportação por tag */
export interface TagScope {
  type: 'tag';
  tag: string;
  notes: Note[];
}

/** Escopo de exportação */
export type ExportScope = DateScope | TagScope;

export interface ExportOptions {
  format: 'text' | 'markdown' | 'json';
  includeReference: boolean;
  groupBy: 'date' | 'tag' | 'none';
}

export const DEFAULT_EXPORT_OPTIONS: ExportOptions = {
  format: 'text',
  includeReference: true,
  groupBy: 'date',
};

/**
 * Gera mensagem formatada a partir de um escopo
 *
 * Formato para data:
 * *Pendências* (ou título customizado)
 *
 * *Tag*
 * - LEITO | nota
 *
 * - LEITO (ref) | nota
 *
 *
 * *Outra tag*
 * - LEITO | nota
 *
 * Formato para tag:
 * *Tag*
 * - LEITO | nota
 *
 * - LEITO (ref) | nota
 *
 * Itens consecutivos são separados por linha em branco (entre o último
 * item de um grupo e o próximo cabeçalho de tag ficam duas linhas em
 * branco). O conteúdo de cada nota é sempre uma única linha: quebras
 * internas de linha são colapsadas em um único espaço. A mensagem não
 * termina com linha em branco.
 */
export function generateMessage(scope: ExportScope): string {
  if (scope.type === 'date') {
    return generateDateMessage(scope);
  }

  return generateTagMessage(scope);
}

/**
 * Gera mensagem para escopo de data
 */
function generateDateMessage(scope: DateScope): string {
  const lines: string[] = [];
  const normalizedTitle = scope.title?.trim();
  const title = normalizedTitle && normalizedTitle.length > 0 ? normalizedTitle : 'Pendências';

  lines.push(`*${title}*`);
  lines.push('');

  for (const tagGroup of scope.tags) {
    lines.push(`*${tagGroup.tag}*`);
    for (const note of tagGroup.notes) {
      lines.push(formatNoteLine(note));
      lines.push('');
    }
    lines.push('');
  }

  return lines.join('\n').trim();
}

/**
 * Gera mensagem para escopo de tag
 */
function generateTagMessage(scope: TagScope): string {
  const lines: string[] = [];

  lines.push(`*${scope.tag}*`);
  lines.push('');

  for (const note of scope.notes) {
    lines.push(formatNoteLine(note));
    lines.push('');
  }

  return lines.join('\n').trim();
}

/**
 * Formata uma linha de nota no formato: - LEITO | nota
 * Inclui referência se existir: - LEITO (ref) | nota
 * Quebras de linha internas da nota são colapsadas em um único espaço.
 */
function formatNoteLine(note: Note): string {
  const bed = note.bed;
  const ref = note.reference ? ` (${note.reference})` : '';
  const content = note.note.replace(/\s*[\r\n]+\s*/g, ' ').trim();
  return `- ${bed}${ref} | ${content}`;
}

/**
 * Exporta notas para formato de texto
 */
export function exportNotesAsText(
  notes: Note[],
  options: ExportOptions = DEFAULT_EXPORT_OPTIONS
): string {
  if (notes.length === 0) {
    return '';
  }

  const lines: string[] = [];

  if (options.groupBy === 'date') {
    const grouped = groupByDate(notes);
    for (const [date, dateNotes] of grouped) {
      lines.push(`📅 ${formatDate(date)}`);
      lines.push('');
      for (const note of dateNotes) {
        lines.push(formatNoteEntry(note, options));
      }
      lines.push('');
    }
  } else if (options.groupBy === 'tag') {
    const grouped = groupByTag(notes);
    for (const [tag, tagNotes] of grouped) {
      lines.push(`🏷️ ${tag}`);
      lines.push('');
      for (const note of tagNotes) {
        lines.push(formatNoteEntry(note, options));
      }
      lines.push('');
    }
  } else {
    for (const note of notes) {
      lines.push(formatNoteEntry(note, options));
    }
  }

  return lines.join('\n').trim();
}

/**
 * Exporta notas para formato Markdown
 */
export function exportNotesAsMarkdown(
  notes: Note[],
  options: ExportOptions = DEFAULT_EXPORT_OPTIONS
): string {
  if (notes.length === 0) {
    return '';
  }

  const lines: string[] = [];
  lines.push('# Notas do Round');
  lines.push('');

  if (options.groupBy === 'date') {
    const grouped = groupByDate(notes);
    for (const [date, dateNotes] of grouped) {
      lines.push(`## ${formatDate(date)}`);
      lines.push('');
      for (const note of dateNotes) {
        lines.push(formatNoteEntryMarkdown(note, options));
      }
    }
  } else if (options.groupBy === 'tag') {
    const grouped = groupByTag(notes);
    for (const [tag, tagNotes] of grouped) {
      lines.push(`## ${tag}`);
      lines.push('');
      for (const note of tagNotes) {
        lines.push(formatNoteEntryMarkdown(note, options));
      }
    }
  } else {
    for (const note of notes) {
      lines.push(formatNoteEntryMarkdown(note, options));
    }
  }

  return lines.join('\n').trim();
}

/**
 * Formata uma entrada de nota (texto simples)
 */
function formatNoteEntry(note: Note, options: ExportOptions): string {
  const header = `Leito ${note.bed}`;
  const ref = options.includeReference && note.reference ? ` (${note.reference})` : '';
  return `${header}${ref}: ${note.note}`;
}

/**
 * Formata uma entrada de nota (Markdown)
 */
function formatNoteEntryMarkdown(note: Note, options: ExportOptions): string {
  const header = `### Leito ${note.bed}`;
  const ref = options.includeReference && note.reference ? ` _(${note.reference})_` : '';
  return `${header}${ref}\n\n${note.note}\n`;
}

/**
 * Agrupa notas por data
 */
function groupByDate(notes: Note[]): Map<string, Note[]> {
  const grouped = new Map<string, Note[]>();
  const sorted = [...notes].sort((a, b) => b.date.localeCompare(a.date));

  for (const note of sorted) {
    const existing = grouped.get(note.date) ?? [];
    existing.push(note);
    grouped.set(note.date, existing);
  }

  return grouped;
}

/**
 * Agrupa notas por tag (fan-out simples: nota com múltiplas tags aparece em múltiplos grupos)
 */
function groupByTag(notes: Note[]): Map<string, Note[]> {
  const grouped = new Map<string, Note[]>();
  const sorted = [...notes].sort((a, b) => {
    const tagsA = a.tags ?? [];
    const tagsB = b.tags ?? [];
    const firstTagA = tagsA[0] ?? '';
    const firstTagB = tagsB[0] ?? '';
    return firstTagA.localeCompare(firstTagB);
  });

  for (const note of sorted) {
    const tags = note.tags ?? [];
    if (tags.length === 0) continue; // Ignora notas sem tags válidas

    for (const tag of tags) {
      const existing = grouped.get(tag) ?? [];
      existing.push(note);
      grouped.set(tag, existing);
    }
  }

  return grouped;
}

/**
 * Formata data para exibição
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Copia texto para a área de transferência
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback para browsers mais antigos
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textarea);
    return success;
  }
}
